import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost } from "@vimaana/ui";
import { fehlertext } from "../lib/fehler";

/**
 * Neuen Kunden direkt im Wizard anlegen.
 *
 * Testlauf 06.10.2026: Neue Kunden gab es nur in Kontor — Medienbruch, und der
 * Wizard fand den frischen Kunden danach nicht. Dieselbe Schnittstelle wie
 * Kontor (`POST /api/pflege/vertraege/kunden` ruft die Kontor-Anlage), aber nur die Felder, die ein Pflegevertrag
 * braucht: Name, Anrede, Anschrift des Gartens, Mail, Telefon. Alles Weitere
 * (Ansprechpartner, Rechnungsadresse) pflegt Kontor.
 */
type Werte = {
  kundentyp: "privat" | "firma";
  anrede: "" | "frau" | "herr" | "familie";
  vorname: string;
  nachname: string;
  firma: string;
  strasse: string;
  plz: string;
  ort: string;
  email: string;
  phone: string;
};

function name(w: Werte): string {
  if (w.kundentyp === "firma") return w.firma.trim();
  const vor = w.vorname.trim();
  const nach = w.nachname.trim();
  if (w.anrede === "familie" && !vor && nach) return `Familie ${nach}`;
  return [vor, nach].filter(Boolean).join(" ");
}

export default function KundeNeu({ vorschlag, onAngelegt, onAbbrechen }: {
  vorschlag: string;
  onAngelegt: (id: string) => void;
  onAbbrechen: () => void;
}) {
  const qc = useQueryClient();
  const teile = vorschlag.trim().split(/\s+/).filter(Boolean);
  const [w, setW] = useState<Werte>({
    kundentyp: "privat", anrede: "",
    vorname: teile.length > 1 ? teile.slice(0, -1).join(" ") : "",
    nachname: teile.length > 1 ? teile[teile.length - 1] : (teile[0] || ""),
    firma: "", strasse: "", plz: "", ort: "", email: "", phone: "",
  });
  const [fehler, setFehler] = useState("");
  const set = <K extends keyof Werte>(k: K, v: Werte[K]) => setW(alt => ({ ...alt, [k]: v }));

  const anlegen = useMutation({
    mutationFn: () => {
      const s = w.strasse.trim();
      const rest = `${w.plz.trim()} ${w.ort.trim()}`.trim();
      return apiPost<{ id: string }>("/api/pflege/vertraege/kunden", {
        name: name(w),
        kundentyp: w.kundentyp,
        typ: w.kundentyp === "privat" ? "Privat" : "Firma",
        anrede: w.kundentyp === "privat" ? w.anrede : "",
        vorname: w.kundentyp === "privat" ? w.vorname.trim() : "",
        nachname: w.kundentyp === "privat" ? w.nachname.trim() : "",
        email: w.email.trim(),
        phone: w.phone.trim(),
        strasse: s, plz: w.plz.trim(), ort: w.ort.trim(),
        lieferadresse: s && rest ? `${s}\n${rest}` : (s || rest),
      });
    },
    onSuccess: k => {
      qc.invalidateQueries({ queryKey: ["pflege"] });
      onAngelegt(String(k.id));
    },
    onError: e => setFehler(fehlertext(e)),
  });

  function speichern() {
    if (!name(w)) { setFehler(w.kundentyp === "firma" ? "Der Firmenname fehlt." : "Vor- oder Nachname fehlt."); return; }
    if (!w.strasse.trim() || !w.ort.trim()) { setFehler("Die Anschrift des Gartens fehlt."); return; }
    setFehler("");
    anlegen.mutate();
  }

  const feld = (label: string, k: keyof Werte, typ = "text", breit = false) => (
    <label className="pf-field" style={breit ? { gridColumn: "1 / -1" } : undefined}>
      <span>{label}</span>
      <input className="pf-input is-tight" type={typ} value={String(w[k])}
        onChange={e => set(k, e.target.value as never)} />
    </label>
  );

  return (
    <div className="pf-card" style={{ marginTop: 10 }}>
      <div style={{ fontWeight: 700, marginBottom: 10 }}>Neuer Kunde</div>
      <div style={{ display: "flex", gap: 16, marginBottom: 10, fontSize: 14 }}>
        <label><input type="radio" checked={w.kundentyp === "privat"} onChange={() => set("kundentyp", "privat")} /> Privatkunde</label>
        <label><input type="radio" checked={w.kundentyp === "firma"} onChange={() => set("kundentyp", "firma")} /> Firma</label>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "0 12px" }}>
        {w.kundentyp === "firma" ? feld("Firmenname *", "firma", "text", true) : (
          <>
            <label className="pf-field">
              <span>Anrede</span>
              <select className="pf-input is-tight" value={w.anrede}
                onChange={e => set("anrede", e.target.value as Werte["anrede"])}>
                <option value="">—</option>
                <option value="frau">Frau</option>
                <option value="herr">Herr</option>
                <option value="familie">Familie</option>
              </select>
            </label>
            {feld("Vorname", "vorname")}
            {feld("Nachname *", "nachname")}
          </>
        )}
        {feld("Straße und Hausnummer *", "strasse", "text", true)}
        {feld("PLZ", "plz")}
        {feld("Ort *", "ort")}
        {feld("E-Mail", "email", "email")}
        {feld("Telefon", "phone", "tel")}
      </div>
      {fehler && <div style={{ color: "var(--pf-red)", fontSize: 13, marginTop: 4 }}>{fehler}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button className="pf-btn" disabled={anlegen.isPending} onClick={speichern}>
          {anlegen.isPending ? "Lege an…" : "Kunde anlegen und wählen"}
        </button>
        <button className="pf-btn is-ghost" onClick={onAbbrechen}>Abbrechen</button>
      </div>
      <div style={{ fontSize: 12, color: "var(--pf-ink-3)", marginTop: 8 }}>
        Der Kunde landet auch in Kontor. Ansprechpartner und Rechnungsadresse dort ergänzen.
      </div>
    </div>
  );
}
